import React, { useEffect, useMemo, useState } from 'react'
import { Button, Icon, Input, Modal, Spin, message } from 'antd'
import {
  generateImageCraiyon,
  generateImageDeepAI,
  getTaskStatus
} from '../../../../services/api/aiVideoGeneration'
import { createPayload } from '../ImageGeneration/utils/payloadHelpers'
import { FRAME_LABELS } from './frameUtils'
import './ShotFrameModal.scss'

const { TextArea } = Input

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const waitForTaskSuccess = async taskId => {
  if (!taskId) {
    throw new Error('No task id returned from image generation')
  }

  const maxAttempts = 60

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const response = await getTaskStatus(taskId)
    const status = response?.data?.status

    if (status === 'SUCCESS') {
      const result = response?.data?.result
      const outputUrl =
        result?.output_url ||
        result?.image_url ||
        result?.url ||
        response?.data?.output_url

      if (!outputUrl) {
        throw new Error('Generated image URL was not returned')
      }

      return outputUrl
    }

    if (status === 'FAILURE' || status === 'ERROR') {
      throw new Error(response?.data?.error || 'Frame generation failed')
    }

    await delay(10000)
  }

  throw new Error('Frame generation timed out')
}

const ShotFrameGenerateModal = ({
  visible,
  shot,
  frameRole,
  frame,
  fallbackFrame,
  formData,
  imageProvider,
  videoId,
  disabled = false,
  onCancel,
  onGenerated
}) => {
  const [prompt, setPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  const basePrompt = useMemo(
    () =>
      frame?.prompt ||
      fallbackFrame?.prompt ||
      shot?.initial_image_prompt ||
      shot?.image_prompt ||
      '',
    [fallbackFrame, frame, shot]
  )

  useEffect(() => {
    if (visible) {
      setPrompt(basePrompt)
    }
  }, [visible, basePrompt])

  const handleGenerate = async () => {
    const trimmedPrompt = prompt.trim()

    if (!trimmedPrompt) {
      message.warning('Add a prompt before generating this frame.')
      return
    }

    setIsGenerating(true)

    try {
      const draftShot = {
        ...shot,
        initial_image_prompt: trimmedPrompt
      }
      const payload = createPayload(draftShot, formData || {}, videoId)
      const provider = String(imageProvider || '').toLowerCase()
      const request =
        provider === 'craiyon'
          ? generateImageCraiyon(payload)
          : generateImageDeepAI(payload)
      const response = await request
      const outputUrl = await waitForTaskSuccess(response?.data?.task_id)

      if (onGenerated) {
        onGenerated(outputUrl, trimmedPrompt)
      }

      message.success(`${FRAME_LABELS[frameRole]} generated`)
    } catch (error) {
      message.error(error?.message || 'Frame generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Modal
      visible={visible}
      title={`Generate ${FRAME_LABELS[frameRole]}`}
      centered
      destroyOnClose
      width={640}
      className="shot-frame-generate-modal"
      onCancel={isGenerating ? undefined : onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={isGenerating}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon="thunderbolt"
          loading={isGenerating}
          disabled={disabled}
          onClick={handleGenerate}
        >
          Generate
        </Button>
      ]}
    >
      <div className="shot-frame-generate-modal__body">
        <div className="shot-frame-generate-modal__field">
          <div className="shot-frame-generate-modal__label-row">
            <label>Prompt</label>
            <Button
              size="small"
              icon="rollback"
              onClick={() => setPrompt(basePrompt)}
              disabled={isGenerating || !basePrompt}
            >
              Use Shot Prompt
            </Button>
          </div>
          <TextArea
            autoSize={{ minRows: 6, maxRows: 10 }}
            value={prompt}
            placeholder="Describe the frame you want to generate"
            disabled={isGenerating || disabled}
            onChange={event => setPrompt(event.target.value)}
          />
        </div>

        {isGenerating && (
          <div className="shot-frame-generate-modal__status">
            <Spin />
            <span>
              Generating {FRAME_LABELS[frameRole]?.toLowerCase()}...
            </span>
          </div>
        )}

        <div className="shot-frame-generate-modal__hint">
          <Icon type="info-circle" />
          <span>
            This only changes the selected frame image. The shot prompt stays
            unchanged.
          </span>
        </div>
      </div>
    </Modal>
  )
}

export default ShotFrameGenerateModal
